import { useState } from 'react';
import { Check, ChevronDown, Image, ImagePlus, Mail } from 'lucide-react';
import { EmailsTrocaDialog } from './EmailsTrocaDialog';
import { canonicalizePhoneBR } from '@/utils/phone-canonical';
import { linhaEstaValidada, reunirLinhasEsim } from './ativacaoResumo';
import { avisoCurtoEsim, COR_ESTADO_ESIM, estadoEsim, FUNDO_ESTADO_ESIM, ROTULO_ESTADO_ESIM, statusTriagemEsim } from '../kanban/fluxo/statusTriagemEsim';
import { ROTULO_ETAPA, type EtapaValidacao } from '@/hooks/useValidacaoEsimVisao';

type Linha = ReturnType<typeof reunirLinhasEsim>[number];
/** O botão da vez de cada linha. Quem decide QUAL é o modal, que tem a conversa
 *  e o envio; a tabela só desenha. `confirmando` = o primeiro clique já foi:
 *  o próximo manda WhatsApp ao cliente. */
export interface AcaoLinhaEsim { rotulo: string; confirmando: boolean; desabilitado: boolean; motivo?: string; executar: () => void }

/**
 * ⛔ A LINHA QUE ESPERA A OPERADORA NÃO É TRABALHO DO TIME (Douglas 23/09/2026).
 *
 * A tabela antiga mostrava a linha em PORTABILIDADE AGENDADA com "Validada:
 * Não" — IDÊNTICA à linha que a Qualidade precisa conferir. Era isso que fazia
 * o pedido parecer travado: 27 linhas prontas para trabalhar, misturadas com
 * 27 que só a operadora destrava.
 *
 * O corte é `entrada_em`, não um palpite: medido em 23/09, as 3.552 linhas COM
 * ela são todas de item ativado, e as 251 SEM ela são todas de item que ainda
 * não ativou. Zero exceção.
 *
 * Os três números saem do MESMO array que desenha as linhas — tile e lista não
 * podem discordar.
 */
function telefoneDe(l: Linha) {
  const canon = canonicalizePhoneBR(l.telefone || '');
  return /^55\d{10,11}$/.test(canon) ? `(${canon.slice(2, 4)}) ${canon.slice(4, -4)}-${canon.slice(-4)}` : l.telefone || 'Número novo';
}
function tipoDe(l: Linha) {
  return l.tipo === 'esim_real' ? 'eSIM real' : l.tipo === 'esim_simcard' ? 'Simcard' : 'Classificar';
}
/** O ICCID conferido fica NA LINHA, onde se confere — não só no detalhe. */
function iccidDe(l: Linha) {
  return l.iccid && l.conferido_em ? `ICCID …${l.iccid.slice(-4)}` : '';
}

/** Há quanto tempo — mesma escrita do backend (situacao-esim.ts). */
function haQuanto(v: string | null | undefined, agora = Date.now()) {
  if (!v) return '';
  const horas = Math.max(0, Math.floor((agora - new Date(v).getTime()) / 3600000));
  return horas >= 48 ? `há ${Math.floor(horas / 24)} dias` : horas >= 1 ? `há ${horas}h` : 'agora há pouco';
}

export function AtivacaoLinhasTabela({ linhas, selecionada, ocupado, selecionar, acao }: {
  linhas: Linha[]; selecionada: string; ocupado: boolean; selecionar: (linha: Linha, foto: boolean) => void;
  acao?: (linha: Linha) => AcaoLinhaEsim | null;
}) {
  const pedidos = [...new Map(linhas.map(l => [l.familiaId, l.pedidoNumero])).entries()];
  const [emailsDe, setEmailsDe] = useState<Linha | null>(null);
  return <div className="space-y-3" aria-label="Linhas para conferência de eSIM">
    {emailsDe && <EmailsTrocaDialog linhaId={emailsDe.id} revisao={emailsDe.revisao} telefone={telefoneDe(emailsDe)}
      aberto={!!emailsDe} fechar={() => setEmailsDe(null)} />}
    {pedidos.map(([familiaId, numero]) => {
      const doPedido = linhas.filter(l => l.familiaId === familiaId);
      const validadas = doPedido.filter(linhaEstaValidada);
      const naOperadora = doPedido.filter(l => !l.entrada_em && !linhaEstaValidada(l));
      const conferir = doPedido.filter(l => l.entrada_em && !linhaEstaValidada(l));
      // ⛔ CADA LINHA DIZ ONDE ANDA (Douglas 23/09/2026): "eu não sei onde anda
      // estas 3 linhas no back... é trabalhar no escuro". O texto é o MESMO da
      // triagem e da Visão (situacao-esim.ts no backend, via statusTriagemEsim);
      // a cor é o MESMO semáforo (Parado vermelho = alguém tem que agir). O
      // último contato é a prova de que a pergunta saiu — e quando.
      const linhaDe = (l: Linha, mudo = false) => {
        const telefone = telefoneDe(l);
        if (mudo) return <div key={l.id} className={`border-t border-border-warm py-1.5 ${selecionada === l.id ? 'bg-muted -mx-3 px-3' : ''}`}>
          <div className="grid h-7 grid-cols-[minmax(0,1fr)_78px_minmax(0,118px)] items-center gap-2">
            <button type="button" disabled={ocupado} aria-label={`Abrir linha ${telefone}`} aria-expanded={selecionada === l.id}
              title={l.operadora ? `Operadora ${l.operadora}` : undefined}
              className="truncate text-left text-[13px] tabular-nums text-muted-foreground hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => selecionar(l, false)}>{telefone}</button>
            <span className="truncate text-[11px] text-muted-foreground" title={l.iccid && l.conferido_em ? `ICCID ${l.iccid} conferido` : undefined}>{tipoDe(l)}{iccidDe(l) && ` · ${iccidDe(l).replace('ICCID ', '')}`}</span>
            <span className="truncate text-right text-[11px] text-muted-foreground" title={l.status_comercial || undefined}>{l.status_comercial || 'não informado'}</span>
          </div>
        </div>;
        const estado = estadoEsim(l);
        const aviso = avisoCurtoEsim(l);
        const acaoDaLinha = acao?.(l) || null;
        return <div key={l.id} className={`border-t border-border-warm py-2 ${selecionada === l.id ? 'bg-muted -mx-3 px-3' : ''}`}>
          <div className="flex h-7 items-center gap-2">
            <span className={`h-2 w-2 shrink-0 rounded-full ${FUNDO_ESTADO_ESIM[estado]}`} title={ROTULO_ESTADO_ESIM[estado]} aria-label={ROTULO_ESTADO_ESIM[estado]} />
            <button type="button" disabled={ocupado} aria-label={`Abrir linha ${telefone}`} aria-expanded={selecionada === l.id}
              title={l.operadora ? `Operadora ${l.operadora}` : undefined}
              className="min-w-0 truncate text-left font-display text-[13px] font-semibold tabular-nums hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => selecionar(l, false)}>{telefone}</button>
            <button type="button" disabled={ocupado} className="shrink-0 text-[11px] text-muted-foreground hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => selecionar(l, false)}>{tipoDe(l)}</button>
            {iccidDe(l) && <span className="inline-flex shrink-0 items-center gap-0.5 font-mono text-[10.5px] text-muted-foreground" title={`ICCID ${l.iccid} conferido`}>{iccidDe(l)}<Check className="h-3 w-3 text-[#075E54] dark:text-[#25D366]" aria-label="conferido" /></span>}
            {l.tipo === 'esim_simcard' && <button type="button" disabled={ocupado} aria-label={`${l.foto_mensagem_id ? 'Ver ou trocar' : 'Vincular'} foto da linha ${telefone}`}
              title={l.foto_mensagem_id ? 'Ver ou trocar foto do chip' : 'Vincular foto do chip'}
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-primary hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => selecionar(l, true)}>{l.foto_mensagem_id ? <Image className="h-3.5 w-3.5" /> : <ImagePlus className="h-3.5 w-3.5" />}</button>}
            {/* Os e-mails da troca, junto do chip que eles pediram (Douglas 24/09/2026). */}
            {l.troca_status && <button type="button" aria-label={`E-mails da troca da linha ${telefone}`} title="E-mails da troca com a gerente"
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-primary hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => setEmailsDe(l)}><Mail className="h-3.5 w-3.5" /></button>}
            {acaoDaLinha && <button type="button" disabled={ocupado || acaoDaLinha.desabilitado} title={acaoDaLinha.motivo}
              className={`ml-auto h-[26px] shrink-0 whitespace-nowrap rounded-md border px-2 text-[11px] font-semibold focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 ${
                acaoDaLinha.confirmando ? 'border-primary bg-primary text-primary-foreground' : 'border-primary text-primary hover:bg-primary/[0.06]'}`}
              onClick={acaoDaLinha.executar}>{acaoDaLinha.confirmando ? 'Confirmar envio' : acaoDaLinha.rotulo}</button>}
          </div>
          {/* O status com o nome da etapa da Visão Prime — a mesma língua da
              triagem. O detalhe ("pediu ajuda há 10h · falta reperguntar")
              vem logo abaixo, porque aqui é o lugar de trabalhar a linha. */}
          <p className={`truncate pl-4 text-[11.5px] font-semibold ${COR_ESTADO_ESIM[estado]}`} title={statusTriagemEsim(l as never)}>
            {l.etapa && ROTULO_ETAPA[l.etapa as EtapaValidacao] ? ROTULO_ETAPA[l.etapa as EtapaValidacao] : statusTriagemEsim(l as never)}</p>
          {l.etapa && <p className="truncate pl-4 text-[10.5px] text-muted-foreground">{statusTriagemEsim(l as never)}</p>}
          <p className={`truncate pl-4 text-[10.5px] ${aviso.vazio || aviso.incerto ? 'text-destructive' : 'text-muted-foreground'}`}
            title={l.iccid ? `ICCID ${l.iccid}` : undefined}>
            {aviso.vazio ? 'Nada enviado ao cliente ainda' : `Último contato: ${aviso.texto}${aviso.incerto ? '' : ` · ${haQuanto(l.aviso_em)}`}`}
          </p>
        </div>;
      };
      return <section key={familiaId} className="overflow-hidden rounded-xl border border-border-warm bg-card">
        <header className="flex h-8 items-center justify-between bg-[#FAF8F4] px-3 dark:bg-[#241A1A]">
          <span className="text-[10px] font-bold uppercase tracking-[1.2px] text-muted-foreground">Pedido {numero}</span>
          <span className="text-[10.5px] text-muted-foreground">{doPedido.length} {doPedido.length === 1 ? 'linha' : 'linhas'}</span>
        </header>
        {doPedido.length > 1 && <div className="grid grid-cols-3 border-b border-border-warm">
          <div className="border-r border-border-warm px-3 py-2">
            <p className="font-display text-[20px] leading-none tabular-nums text-primary">{conferir.length}</p>
            <p className="mt-1 text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">Para conferir</p>
          </div>
          <div className="border-r border-border-warm px-3 py-2">
            <p className="font-display text-[20px] leading-none tabular-nums text-[#075E54] dark:text-[#25D366]">{validadas.length}</p>
            <p className="mt-1 text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">Validadas</p>
          </div>
          <div className="px-3 py-2">
            <p className="font-display text-[20px] leading-none tabular-nums text-muted-foreground">{naOperadora.length}</p>
            <p className="mt-1 text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">Na operadora</p>
          </div>
        </div>}
        {!!conferir.length && <div className="px-3 pb-1 pt-2">
          <p className="text-[9.5px] font-bold uppercase tracking-wider text-primary">Para conferir · é com você</p>
          {conferir.map(l => linhaDe(l))}
        </div>}
        {!!validadas.length && <details className="group border-t border-border-warm px-3">
          <summary className="flex h-8 cursor-pointer list-none items-center gap-1.5 text-[11.5px] text-[#075E54] dark:text-[#25D366]">
            <Check className="h-3.5 w-3.5 shrink-0" />
            {validadas.length} {validadas.length === 1 ? 'linha validada' : 'linhas validadas'}
            <ChevronDown className="ml-auto h-3.5 w-3.5 text-muted-foreground transition-transform group-open:rotate-180" />
          </summary>
          <div className="pb-1">{validadas.map(l => linhaDe(l))}</div>
        </details>}
        {!!naOperadora.length && <div className="border-t border-border-warm px-3 pb-2 pt-2">
          <p className="text-[9.5px] font-bold uppercase tracking-wider text-muted-foreground">Esperando a operadora · não é com você</p>
          {naOperadora.map(l => linhaDe(l, true))}
          <p className="pt-1 text-[10.5px] text-muted-foreground">Entram sozinhas na conferência quando ativarem. Já dá para preparar a foto e o ICCID.</p>
        </div>}
      </section>;
    })}
  </div>;
}
